import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate, useParams, Link, useLocation } from 'react-router-dom';
import { Container, Row, Col, Form, InputGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faUpload, faXmark } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { DataManager } from '../../core/data-manager';
import { theme } from '../../config/theme';
import { LanguageProvider } from '../../core/language-provider';

// Dimensione massima file consentita (allineata al backend: settings.json -> max_size)
const MAX_FILE_SIZE_BYTES = 10242880;

export default function JobApply() {
    const { blue, lightBlue } = theme;
    const navigate = useNavigate();
    const location = useLocation();
    const { k } = useParams();
    const id = parseInt(k);

    const [job, setJob] = useState(null);
    const [form, setForm] = useState([]);
    const title = job?.title || job?.name || `${LanguageProvider.get('pages.jobApply.position')} #${id}`;

    const degreeInputRef = useRef(null);
    const cvInputRef = useRef(null);

    useEffect(() => {
        loadJob();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    async function loadJob() {
        const res = await DataManager.getItem('job_post', id);
        if (res && res.success === 1) {
            const item = Array.isArray(res.body) ? res.body[0] : res.body;
            console.log(item)
            setJob(item);
        }
    }

    // Stato per mostrare i valori nei campi (keyed by questionId)
    const [formValues, setFormValues] = useState({});

    // Refs per gli <input type="file"> per ogni domanda file
    const fileInputsRef = useRef({});

    // Rehydrate form from navigation state (coming back from review)
    useEffect(() => {
        const incoming = location.state?.form;
        if (!incoming || !Array.isArray(incoming) || incoming.length === 0) return;
        // Set raw form entries
        setForm(incoming);
        // Build display values (strings and file names)
        const values = {};
        for (const a of incoming) {
            if (!a) continue;
            if (a.type === '10_FILE') {
                const v = a.value;
                if (v && typeof v === 'object') {
                    if ('name' in v && v.name) values[a.idQuestion] = v.name;
                    else if ('originalFileName' in v && v.originalFileName) values[a.idQuestion] = v.originalFileName;
                }
            } else {
                values[a.idQuestion] = a.value ?? '';
            }
        }
        setFormValues(values);
        // Clean state to avoid carrying on through further navigations
        // history state remains, but we won't depend on it afterward
    }, [location.state]);

    const upsertAnswer = (item, value) => {
        setForm(prev => {
            const idx = prev.findIndex(a => a.idQuestion === item.id);
            const entry = {
                idQuestion: item.id,
                type: item.type?.value,
                value, // per i file è direttamente il File (o null)
            };
            if (idx >= 0) {
                const copy = [...prev];
                copy[idx] = entry;
                return copy;
            }
            return [...prev, entry];
        });
        console.log(form)
    };

    const handleChange = (item) => (e) => {
        const value = e.target.value;
        setFormValues(prev => ({ ...prev, [item.id]: value }));
        upsertAnswer(item, value);
    };

    const handleFileChange = (item) => (e) => {
        const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
        // Controllo dimensione massima consentita
        if (file && file.size > MAX_FILE_SIZE_BYTES) {
            toast.error(
                `${LanguageProvider.get('pages.jobApply.fileTooLarge')} (10 MB)`,
                { theme: 'colored' }
            );
            // reset dell'input così l'utente può riprovare con lo stesso file
            const input = fileInputsRef.current[item.id];
            if (input) input.value = "";
            return;
        }
        // per mostrare il nome file nel campo di testo
        setFormValues(prev => ({ ...prev, [item.id]: file ? file.name : "" }));
        upsertAnswer(item, file); // salva l'intero File; l'upload avverrà in review
    };

    const triggerUpload = (itemId) => {
        fileInputsRef.current[itemId]?.click();
    };

    const clearFile = (item) => {
        // reset input file
        const input = fileInputsRef.current[item.id];
        if (input) input.value = "";
        setFormValues(prev => ({ ...prev, [item.id]: "" }));
        upsertAnswer(item, null); // rimuove il file salvando null
    };

    const isFormComplete = useMemo(() => {
        if (!job?.questions || job.questions.length === 0) return false;
        // For each question, ensure form has a non-empty answer according to type
        return job.questions.every(q => {
            const ans = form.find(a => a.idQuestion === q.id);
            const type = q?.type?.value;
            if (!ans) return false;
            const v = ans.value;
            if (type === '10_FILE') {
                // Accept File object with a name (upload deferred to review step)
                return !!(v && (typeof v === 'object') && ('name' in v) && v.name);
            }
            // String, Text, Date must be non-empty string
            return v !== null && v !== undefined && String(v).trim() !== '';
        });
    }, [job, form]);





    return (
        <Container fluid>
            <Container fluid={'md'}>
                {/* Title */}
                <Row className='pt-5 pb-3 align-items-center mb-4'>
                    <Col>
                        <div className='text-muted'>{LanguageProvider.get('pages.jobApply.applyingFor')}</div>
                        <div className='d-flex align-items-center mt-1'>
                            <h2 className='mb-0' style={{ color: blue }}>{title}</h2>
                        </div>
                    </Col>
                </Row>

                {/* Stepper */}
                <Row className='mb-4'>
                    <Col>
                        {(() => {
                            const steps = [
                                LanguageProvider.get('pages.jobApply.steps.loginRegister'),
                                LanguageProvider.get('pages.jobApply.steps.additionalData'),
                                LanguageProvider.get('pages.jobApply.steps.reviewAndSend')
                            ];
                            const current = 2;
                            return (
                                <>
                                    {/* Mobile compact */}
                                    <div className='d-md-none' style={{ color: blue, fontWeight: 600 }}>
                                        {current}/{steps.length} {steps[current - 1]}
                                    </div>
                                    {/* Desktop stepper */}
                                    <div className='d-none d-md-flex align-items-center gap-3 flex-wrap' style={{ color: blue }}>
                                        {steps.map((label, i) => {
                                            const idx = i + 1;
                                            const isActive = idx === current;
                                            const circleStyle = {
                                                backgroundColor: isActive ? lightBlue : 'transparent',
                                                color: isActive ? 'white' : blue,
                                                border: isActive ? `1px solid ${lightBlue}` : `1px solid ${'#e6edf5'}`,
                                                width: 28,
                                                height: 28,
                                                borderRadius: 4,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 600
                                            };
                                            return (
                                                <div key={label} className='d-flex align-items-center gap-2'>
                                                    <div style={circleStyle}>{idx}</div>
                                                    <div className='me-2'>{label}</div>
                                                    {idx < steps.length && <span className='text-muted'>{'>'}</span>}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            );
                        })()}
                    </Col>
                </Row>

                <hr className='my-4' />


                <Container>
                    {job?.questions?.map((item, i) => (
                        <Row key={item.id ?? i} className="align-items-center g-3 mb-3">
                            <Col md={3} xs={12} className="text-md-start text-muted">
                                {item.title}
                            </Col>
                            <Col md={9} xs={12}>
                                {item?.type?.value === "0_STRING" ? (
                                    <Form.Control
                                        placeholder={`${item.title}...`}
                                        value={formValues[item.id] ?? ""}
                                        onChange={handleChange(item)}
                                    />
                                ) : null}

                                {item?.type?.value === "10_FILE" ? (
                                    <div className="d-flex flex-column flex-md-row gap-2">
                                        <Button
                                            variant="outline-secondary"
                                            onClick={() => triggerUpload(item.id)}
                                            className="flex-shrink-0"
                                        >
                                            <FontAwesomeIcon icon={faUpload} className="me-2" /> {LanguageProvider.get('pages.jobApply.uploadFile')}
                                        </Button>

                                        <Form.Control
                                            placeholder={LanguageProvider.get('pages.jobApply.filePlaceholder')}
                                            value={formValues[item.id] ?? ""}
                                            readOnly
                                        />

                                        <Button
                                            variant="light"
                                            onClick={() => clearFile(item)}
                                            disabled={!(formValues[item.id] ?? "")}
                                            className="flex-shrink-0"
                                        >
                                            {LanguageProvider.get('pages.jobApply.removeFile')}
                                        </Button>

                                        <Form.Control
                                            ref={(el) => (fileInputsRef.current[item.id] = el)}
                                            type="file"
                                            accept=".pdf,application/pdf"
                                            className="d-none"
                                            onChange={handleFileChange(item)}
                                        />
                                    </div>
                                ) : null}

                                {item?.type?.value === "20_TEXT" ? (
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder={item.title}
                                        value={formValues[item.id] ?? ""}
                                        onChange={handleChange(item)}
                                    />
                                ) : null}

                                {item?.type?.value === "30_DATA" ? (
                                    <Form.Control
                                        type="date"
                                        value={formValues[item.id] ?? ""}
                                        onChange={handleChange(item)}
                                    />
                                ) : null}
                            </Col>
                        </Row>
                    ))}
                </Container>


                <hr className='my-4' />

                {/* Actions */}
                <Row className='pb-5 g-3'>
                    <Col md={{ span: 3, offset: 3 }} xs={12}>
                        <Button variant='outline-secondary' className='w-100' onClick={() => navigate(`/job/${id}`)}>
                            <FontAwesomeIcon icon={faXmark} className='me-2' /> {LanguageProvider.get('pages.jobApply.cancel')}
                        </Button>
                    </Col>
                    <Col md={6} xs={12}>
                        <Button
                            className='w-100'
                            style={{ backgroundColor: lightBlue, borderColor: lightBlue }}
                            disabled={!isFormComplete}
                            onClick={() => navigate(`/job/${id}/apply/review`, { state: { form } })}
                        >
                            {LanguageProvider.get('pages.jobApply.next')} <FontAwesomeIcon icon={faArrowRight} className='ms-2' />
                        </Button>
                    </Col>
                </Row>
            </Container>
        </Container>
    );
}
